import { useContext, useMemo } from 'react';
import { faCheckCircle } from '@fortawesome/pro-regular-svg-icons';
import { MailingsTrackableOptions } from 'components/filter/MailingsTrackableOptions';
import { PaymentStatusOptions } from 'components/filter/PaymentStatusOptions';
import { PlanStatusOptions } from 'components/filter/PlanStatusOptions';
import { PledgeStatusOptions } from 'components/filter/PledgeStatusOptions';
import { TicketStatusOptions } from 'components/filter/TicketStatusOptions';
import { FilterComboboxItems } from 'components/filter/filters-next/FilterComboboxItems';
import { FilterContainer } from 'components/filter/filters-next/FilterContainer';
import { FilterContext } from 'components/filter/filters-next/FilterContext';
import { FilterForm } from 'components/filter/filters-next/FilterForm';
import { FilterFormContext } from 'components/filter/filters-next/FilterFormContext';
import { FilterSubmit } from 'components/filter/filters-next/FilterSubmit';
import { ReadableFilterValue } from 'components/filter/filters-next/ReadableFilterValue';
import { transformMultiselectToReadable } from 'components/filter/filters-next/readable';
import { getCacheKeyFromRelationalKey } from 'components/filter/filters-next/relational';
import { useCurrentFilter } from 'components/filter/filters-next/useCurrentFilter';
import { useFilter } from 'components/filter/filters-next/useFilter';
import { injectEquals } from 'components/filter/filters-next/util';
import {
    ContentFilterComponent,
    DownstreamFilterTransform,
    FilterFormValue,
    FilterIcon,
    FilterValue,
    LabelFilterComponent,
    PaymentStatus,
    ReadableFilterComponent,
    ServerKey,
    UpstreamFilterTransform,
} from 'components/filter/types';
import {
    zeroMailingsStatusSelected,
    zeroPaymentStatusesSelected,
    zeroPlanStatusesSelected,
    zeroPledgeStatusesSelected,
    zeroTicketStatusSelected,
} from 'components/filter/validations';
import * as Form from 'components/form';
import { capitalize, identity, toLower } from 'lodash';
import { FilterCacheKey } from 'types/filter-cache-key';

interface PaymentStatusFilterFormValues extends FilterFormValue<PaymentStatus[]> {}

export const filterKey: ServerKey[] = ['status', 'statusFriendly'];

export const Icon: FilterIcon = faCheckCircle;

export const Filter: ContentFilterComponent = () => {
    const { defaultValue, ...rest } = useFilter<PaymentStatus[]>();
    const {
        resource: _resource,
        relationalServerKey,
        strategyType: strategyTypeRaw,
    } = useContext(FilterContext);
    const resource = relationalServerKey
        ? getCacheKeyFromRelationalKey(relationalServerKey)
        : _resource;
    const strategyType = strategyTypeRaw as ServerKey;
    const { validateRules, Options } = getPropsByResource(resource);
    const methods = Form.useForm<PaymentStatusFilterFormValues>({
        defaultValues: {
            value: defaultValue ? defaultValue.value : [],
        },
        mode: 'onChange',
    });
    const { control, path } = methods;

    const ctx = useMemo(
        () => ({
            defaultValue,
            ...rest,
        }),
        [defaultValue, rest],
    );

    return (
        <FilterFormContext.Provider value={ctx}>
            <Form.Provider {...methods}>
                <FilterForm>
                    <FilterContainer>
                        <Form.Container>
                            <Form.Label name={path('value')}>
                                <Label strategyType={strategyType} />
                            </Form.Label>
                            <Form.Combobox
                                control={control}
                                name="value"
                                rules={{ validate: validateRules }}
                            >
                                <FilterComboboxItems>
                                    <Options />
                                </FilterComboboxItems>
                            </Form.Combobox>
                            <Form.Error name={path('value')} />
                        </Form.Container>
                        <FilterSubmit />
                    </FilterContainer>
                </FilterForm>
            </Form.Provider>
        </FilterFormContext.Provider>
    );
};
export const Readable: ReadableFilterComponent = () => {
    const currentFilter = useCurrentFilter<PaymentStatus[]>();
    const value = currentFilter?.value ?? [];

    return (
        <ReadableFilterValue>
            {transformMultiselectToReadable(value, {
                transform: v => capitalize(toLower(v.replace(/_/g, ' '))),
            })}
        </ReadableFilterValue>
    );
};

export const Label: LabelFilterComponent = props => {
    const { relationalServerKey } = props;
    const { resource: _resource } = useContext(FilterContext);
    const resource = relationalServerKey
        ? getCacheKeyFromRelationalKey(relationalServerKey)
        : _resource;

    switch (resource) {
        case 'transactions':
            return 'Payment status';
        case 'engage':
        case 'tickets':
        case 'relational-campaigns':
        case 'pledges':
        case 'contacts':
        case 'plans':
        case 'campaigns':
        default:
            return 'Status';
    }
};

const getPropsByResource = (resource: FilterCacheKey) => {
    const props = {
        validateRules: zeroPaymentStatusesSelected,
        Options: PaymentStatusOptions,
    };

    switch (resource) {
        case 'plans':
            props.validateRules = zeroPlanStatusesSelected;
            props.Options = PlanStatusOptions;
            break;
        case 'tickets':
            props.validateRules = zeroTicketStatusSelected;
            props.Options = TicketStatusOptions;
            break;
        case 'pledges':
            props.validateRules = zeroPledgeStatusesSelected;
            props.Options = PledgeStatusOptions;
            break;
        case 'mailingsTracking':
            props.validateRules = zeroMailingsStatusSelected;
            props.Options = MailingsTrackableOptions;
            break;
        default:
            break;
    }

    return props;
};

export const downstream: DownstreamFilterTransform<PaymentStatus> = identity;

export const upstream: UpstreamFilterTransform<PaymentStatus> = (filter: FilterValue) =>
    injectEquals({
        value: filter.value,
    });
